import React from 'react';

export interface UserOption {
  id: number;
  name: string;
  email: string;
  is_house: boolean;
}

interface UserAssignmentRowProps {
  ua: { user_id: number; commission_percentage: number };
  users: UserOption[];
  onUpdate: (field: 'user_id' | 'commission_percentage', value: number) => void;
  onRemove: () => void;
  /**
   * R13 CR HIGH #3: keyboard-a11y fix. When the containing row wraps
   * this in `pointer-events-none`, mouse events are blocked but Tab
   * still reaches the underlying <select>/<input> and users can mutate
   * state that will be silently dropped at Confirm. Passing `disabled`
   * through to each control makes the row truly non-interactive for
   * keyboard users as well.
   */
  disabled?: boolean;
}

/**
 * Single editable row for one (employee, %) pair. Reused across both the
 * new-account and existing-account sections of NewAccountReviewModal so
 * the input layout stays consistent.
 */
export const UserAssignmentRow: React.FC<UserAssignmentRowProps> = ({
  ua,
  users,
  onUpdate,
  onRemove,
  disabled = false,
}) => (
  <div className="flex items-center space-x-2 mb-2">
    <select
      value={ua.user_id || ''}
      onChange={(e) => onUpdate('user_id', parseInt(e.target.value) || 0)}
      className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
      disabled={disabled}
      aria-disabled={disabled}
    >
      <option value="">-- Select Employee --</option>
      {users.map((user) => (
        <option key={user.id} value={user.id}>
          {user.is_house ? `[House] ${user.name}` : user.name}
        </option>
      ))}
    </select>
    <input
      type="number"
      value={ua.commission_percentage}
      onChange={(e) => onUpdate('commission_percentage', parseFloat(e.target.value) || 0)}
      className="w-20 px-2 py-1.5 border border-gray-300 rounded text-sm text-right disabled:bg-gray-100 disabled:cursor-not-allowed"
      min="0"
      max="100"
      step="0.01"
      disabled={disabled}
      aria-disabled={disabled}
    />
    <span className="text-sm text-gray-500">%</span>
    <button
      onClick={onRemove}
      disabled={disabled}
      aria-disabled={disabled}
      className="text-red-500 hover:text-red-700 text-sm px-1 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      &times;
    </button>
  </div>
);
